import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Container,
  Group,
  NumberInput,
  Select,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import { createAdminPartner, getMarketplaces } from "../api";

export default function PartnerCreate() {
  const [saving, setSaving] = useState(false);
  const [marketplaces, setMarketplaces] = useState([]);
  const [, setLocation] = useLocation();

  useEffect(() => {
    getMarketplaces().then((data) => {
      setMarketplaces(
        (data || []).map((item) => ({
          value: item.id,
          label: item.name,
        })),
      );
    });
  }, []);

  const form = useForm({
    initialValues: {
      tenantName: "",
      displayName: "",
      email: "",
      firstName: "",
      lastName: "",
      marketplaceId: "",
      skuPool: 10000,
      offerCurrency: "gbp",
      offerMinorPerSku: 50,
    },
    validate: {
      tenantName: (val) => (val.trim() ? null : "Required"),
      email: (val) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)
          ? null
          : "Invalid email",
      marketplaceId: (val) => (val ? null : "Required"),
      skuPool: (val) => (val >= 1 ? null : "SKU pool must be at least 1"),
      offerCurrency: (val) => (val ? null : "Required"),
      offerMinorPerSku: (val) =>
        val >= 1 ? null : "Must be at least 1 minor unit per SKU",
    },
  });

  return (
    <Container size="sm">
      <Title order={1} mb="lg">
        Create partner
      </Title>
      <form
        onSubmit={form.onSubmit(async (values) => {
          setSaving(true);
          try {
            await createAdminPartner({
              tenantName: values.tenantName.trim(),
              displayName: values.displayName.trim() || values.tenantName.trim(),
              email: values.email.trim(),
              firstName: values.firstName.trim() || null,
              lastName: values.lastName.trim() || null,
              marketplaceId: values.marketplaceId,
              skuPool: values.skuPool,
              offerCurrency: values.offerCurrency,
              offerMinorPerSku: values.offerMinorPerSku,
            });
            notifications.show({
              title: "Partner created",
              message: "An activation email was sent so they can set a password.",
              color: "green",
            });
            setLocation("/admin/partners");
          } finally {
            setSaving(false);
          }
        })}
      >
        <Stack>
          <Alert>
            This creates a MarketplacePartner tenant and sends an activation
            email. They land in the partner portal, not the merchant app. Local
            seed accounts are development-only and are not created here.
          </Alert>
          <TextInput
            label="Tenant name"
            placeholder="B&Q"
            required
            {...form.getInputProps("tenantName")}
          />
          <TextInput
            label="Display name"
            description="Shown in the partner portal. Defaults to the tenant name."
            {...form.getInputProps("displayName")}
          />
          <TextInput
            label="Owner email"
            required
            type="email"
            {...form.getInputProps("email")}
          />
          <Group grow>
            <TextInput
              label="First name"
              {...form.getInputProps("firstName")}
            />
            <TextInput label="Last name" {...form.getInputProps("lastName")} />
          </Group>
          <Select
            label="Marketplace"
            description="Sponsored SKUs spend only on this marketplace."
            required
            searchable
            data={marketplaces}
            {...form.getInputProps("marketplaceId")}
          />
          <NumberInput
            label="SKU pool"
            description="Credits the partner can grant to merchants. 10,000 is the B&Q trial pool."
            required
            min={1}
            thousandSeparator
            {...form.getInputProps("skuPool")}
          />
          <Select
            label="Payment currency"
            description="Copied onto merchants this partner creates. Later changes do not rewrite existing merchants."
            required
            data={[
              { value: "gbp", label: "GBP" },
              { value: "eur", label: "EUR" },
              { value: "usd", label: "USD" },
            ]}
            {...form.getInputProps("offerCurrency")}
          />
          <NumberInput
            label="Minor units per SKU"
            description="50 means 50p, 50 euro cents, or $0.50. B&Q is 50."
            required
            min={1}
            {...form.getInputProps("offerMinorPerSku")}
          />
          <Group justify="space-between">
            <Button
              variant="subtle"
              onClick={() => setLocation("/admin/partners")}
            >
              Back
            </Button>
            <Button type="submit" loading={saving}>
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}
